import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Alert,
  Paper,
  Divider,
  Badge,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Drawer,
  Fab,
  Menu,
  MenuItem,
  ListItemButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Skeleton
} from '@mui/material';
import ImpersonationBanner from './ImpersonationBanner';
import OnboardingChecklist from './OnboardingChecklist';
import { useToast } from './ToastNotifications';
import LoadingSpinner from './LoadingSpinner';
import { SkeletonMetricCard, SkeletonDashboard } from './SkeletonLoader';
import EmptyState from './EmptyState';
import {
  TrendingUp,
  Facebook,
  Instagram,
  LinkedIn,
  YouTube,
  TikTok,
  AutoAwesome,
  Psychology,
  SmartToy,
  ContentPaste,
  Campaign,
  Analytics,
  Notifications,
  CheckCircle,
  Star,
  EmojiEvents,
  Rocket,
  TrendingDown,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  Schedule,
  PlayArrow,
  Lightbulb,
  Psychology as PsychologyIcon,
  Settings,
  School,
  DragIndicator,
  VisibilityOff,
  Add,
  Remove,
  ExpandMore,
  ExpandLess,
  Menu as MenuIcon,
  Close,
  DragHandle,
  MoreVert,
  Edit,
  Delete,
  Refresh,
  Fullscreen,
  FullscreenExit,
  Logout,
  Person,
  Email,
  Phone,
  Business,
  MyLocation,
  People,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  CloudUpload,
  Dashboard as DashboardIcon,
  Create,
  CalendarToday,
  LocationOn,
  Send,
  TrackChanges,
  Insights,
  Speed,
  DataUsage,
  Timeline,
  Assessment,
  AttachMoney,
  Group,
  NotificationsActive,
  PriorityHigh,
  CheckCircleOutline,
  Warning,
  Info,
  ArrowUpward,
  ArrowDownward,
  Launch,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showToast } = useToast();

  // State management - preserving all existing functionality
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userStats, setUserStats] = useState({
    totalLeads: 0,
    activeCampaigns: 0,
    totalRevenue: 0,
    conversionRate: 0,
    engagementRate: 0,
    followers: 0,
    reach: 0,
    impressions: 0,
    level: 1,
    experience: 0,
    nextLevel: 100,
    achievements: []
  });

  const [widgetVisibility, setWidgetVisibility] = useState({
    metrics: true,
    quickActions: true,
    leadGeneration: true,
    recentActivity: true,
    achievements: true,
    performance: true
  });

  const [expandedSections, setExpandedSections] = useState({
    leadGeneration: true,
    recentActivity: true,
    achievements: true
  });

  // Preserve all existing functions
  const handleStopImpersonation = () => {
    setIsImpersonating(false);
    setImpersonatedUser(null);
    showToast('Impersonation stopped', 'success');
  };

  const unlockAchievement = (id, name, icon) => {
    setUserStats(prev => ({
      ...prev,
      achievements: prev.achievements.map(achievement =>
        achievement.id === id ? { ...achievement, unlocked: true } : achievement
      )
    }));
    showToast(`Achievement unlocked: ${name} ${icon}`, 'success');
  };

  const getLevelProgress = () => {
    return (userStats.experience / userStats.nextLevel) * 100;
  };

  const getGrowthColor = (rate) => {
    return rate >= 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  const getEngagementRate = () => {
    return userStats.engagementRate;
  };

  const handleWidgetToggle = (widgetKey) => {
    setWidgetVisibility(prev => ({
      ...prev,
      [widgetKey]: !prev[widgetKey]
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // NEW: Premium Hero Section with Glassmorphism
  const PremiumHeroSection = () => {
    // Get user name from actual user data
    const userName = user?.user_metadata?.full_name || 
                    user?.user_metadata?.name || 
                    user?.email?.split('@')[0] || 
                    'User';
    const currentTime = new Date().getHours();
    const greeting = currentTime < 12 ? 'Good morning' : currentTime < 17 ? 'Good afternoon' : 'Good evening';
    
    return (
      <Box sx={{ 
        mb: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 4,
          zIndex: -1
        }
      }}>
        <Paper 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              borderRadius: '50%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                {greeting}, {userName}! 👋
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 3,
                  fontWeight: 400,
                  opacity: 0.8
                }}
              >
                Your social media empire is thriving today
              </Typography>
              
              {/* Premium Quick Wins */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<CheckCircleOutline />} 
                  label={`${userStats.totalLeads} total leads`} 
                  sx={{ 
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip 
                  icon={<TrendingUp />} 
                  label={`${userStats.activeCampaigns} active campaigns`} 
                  sx={{ 
                    background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip 
                  icon={<Star />} 
                  label={`Level ${userStats.level}`} 
                  sx={{ 
                    background: 'linear-gradient(135deg, #FF9800, #F57C00)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Box>
            </Box>
            
            {/* Premium Level Progress */}
            <Box sx={{ textAlign: 'center', minWidth: 140 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: '2rem',
                    fontWeight: 800,
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                    border: '4px solid white'
                  }}
                >
                  {userStats.level}
                </Avatar>
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: -12, 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    background: 'white',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `3px solid #667eea`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }}
                >
                  <Rocket sx={{ fontSize: 18, color: '#667eea' }} />
                </Box>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  mt: 2, 
                  fontWeight: 700,
                  color: '#667eea'
                }}
              >
                Level {userStats.level}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block',
                  fontWeight: 500
                }}
              >
                {userStats.experience}/{userStats.nextLevel} XP
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // NEW: Premium Metric Cards with Glassmorphism
  const PremiumMetricCard = ({ title, value, subtitle, icon, color, growth, trend, size = 'medium' }) => {
    const isPositive = growth >= 0;
    const isLarge = size === 'large';
    
    return (
      <Card 
        sx={{ 
          height: '100%',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          },
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            '& .metric-icon': {
              transform: 'scale(1.1) rotate(5deg)',
            }
          }
        }}
      >
        <CardContent sx={{ p: isLarge ? 4 : 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box 
              className="metric-icon"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: isLarge ? 64 : 48,
                height: isLarge ? 64 : 48,
                borderRadius: '16px',
                background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
                color: color,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `2px solid ${color}20`
              }}
            >
              {icon}
            </Box>
            {growth !== undefined && (
              <Chip
                icon={isPositive ? <ArrowUpward /> : <ArrowDownward />}
                label={`${Math.abs(growth)}%`}
                size="small"
                sx={{ 
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: isPositive 
                    ? 'linear-gradient(135deg, #4CAF50, #45a049)' 
                    : 'linear-gradient(135deg, #f44336, #d32f2f)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            )}
          </Box>
          
          <Typography 
            variant={isLarge ? 'h2' : 'h3'} 
            sx={{ 
              fontWeight: 800, 
              mb: 1,
              color: 'text.primary',
              fontSize: isLarge ? { xs: '2.5rem', md: '3rem' } : { xs: '2rem', md: '2.5rem' }
            }}
          >
            {value}
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary',
              mb: 1,
              fontWeight: 600
            }}
          >
            {title}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                opacity: 0.7,
                fontWeight: 500
              }}
            >
              {subtitle}
            </Typography>
          )}
          
          {trend && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={trend} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: `${color}20`,
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
                    borderRadius: 3
                  }
                }} 
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // NEW: Premium Metrics Grid
  const PremiumMetricsGrid = () => (
    <Box sx={{ mb: 6 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 800, 
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Performance Overview
      </Typography>
      <Grid container spacing={4}>
        {/* Primary KPI */}
        <Grid item xs={12} md={6} lg={4}>
          <PremiumMetricCard
            title="Total Revenue"
            value={`$${userStats.totalRevenue.toLocaleString()}`}
            subtitle="This month"
            icon={<AttachMoney />}
            color="#4CAF50"
            growth={0}
            size="large"
          />
        </Grid>
        
        {/* Supporting Metrics */}
        <Grid item xs={12} md={6} lg={2}>
          <PremiumMetricCard
            title="Active Leads"
            value={userStats.totalLeads.toLocaleString()}
            subtitle="Qualified prospects"
            icon={<People />}
            color="#2196F3"
            growth={0}
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={2}>
          <PremiumMetricCard
            title="Conversion Rate"
            value={`${userStats.conversionRate}%`}
            subtitle="Lead to customer"
            icon={<TrackChanges />}
            color="#FF9800"
            growth={0}
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={2}>
          <PremiumMetricCard
            title="Engagement"
            value={`${userStats.engagementRate}%`}
            subtitle="Social media"
            icon={<ThumbUp />}
            color="#9C27B0"
            growth={0}
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={2}>
          <PremiumMetricCard
            title="Campaigns"
            value={userStats.activeCampaigns}
            subtitle="Running now"
            icon={<Campaign />}
            color="#607D8B"
            growth={0}
          />
        </Grid>
      </Grid>
    </Box>
  );

  // NEW: Premium Action Center
  const PremiumActionCenter = () => (
    <Box sx={{ mb: 6 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 800, 
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<Add />}
            onClick={() => navigate('/lead-capture')}
            sx={{ 
              py: 3, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Capture Leads
          </Button>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Create />}
            onClick={() => navigate('/content')}
            sx={{ 
              py: 3,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              border: '2px solid #667eea',
              color: '#667eea',
              '&:hover': {
                background: 'rgba(102, 126, 234, 0.08)',
                border: '2px solid #5a6fd8',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Create Content
          </Button>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Analytics />}
            onClick={() => navigate('/analytics')}
            sx={{ 
              py: 3,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              border: '2px solid #667eea',
              color: '#667eea',
              '&:hover': {
                background: 'rgba(102, 126, 234, 0.08)',
                border: '2px solid #5a6fd8',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            View Analytics
          </Button>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Schedule />}
            onClick={() => navigate('/calendar')}
            sx={{ 
              py: 3,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'none',
              border: '2px solid #667eea',
              color: '#667eea',
              '&:hover': {
                background: 'rgba(102, 126, 234, 0.08)',
                border: '2px solid #5a6fd8',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Schedule Posts
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  // NEW: Premium Insights Panel
  const PremiumInsightsPanel = () => (
    <Box sx={{ mb: 6 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 800, 
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Smart Insights
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4CAF50 0%, #45a049 100%)',
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircle color="success" sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                  What's Working Well
                </Typography>
              </Box>
              <List dense>
                {userStats.totalRevenue > 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrendingUp color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Revenue: ${userStats.totalRevenue.toLocaleString()}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Your lead generation campaigns are performing well
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrendingUp color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Ready to start generating revenue
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Set up your first campaign to start earning
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {userStats.totalLeads > 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <People color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {userStats.totalLeads} leads captured
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Great progress on lead generation
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <People color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Start capturing leads
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Use lead capture tools to grow your audience
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {userStats.activeCampaigns > 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Campaign color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {userStats.activeCampaigns} active campaigns
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Your campaigns are running successfully
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Campaign color="success" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Create your first campaign
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Launch campaigns to reach your audience
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #FF9800 0%, #F57C00 100%)',
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Lightbulb color="warning" sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF9800' }}>
                  Opportunities
                </Typography>
              </Box>
              <List dense>
                {userStats.conversionRate === 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrackChanges color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Set up conversion tracking
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Track your first conversions to measure success
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrackChanges color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Improve conversion rate
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Current rate: {userStats.conversionRate}% - aim for 10%+
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {userStats.totalLeads === 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <People color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Start lead generation
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Use lead capture tools to grow your audience
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrendingUp color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Scale lead generation
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          You have {userStats.totalLeads} leads - time to scale up
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {userStats.activeCampaigns === 0 ? (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Campaign color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Launch your first campaign
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Create campaigns to reach your target audience
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AutoAwesome color="warning" sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Optimize campaigns
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          Use AI to improve your {userStats.activeCampaigns} active campaigns
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // NEW: Premium Recent Activity
  const PremiumRecentActivity = () => (
    <Box sx={{ mb: 6 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 800, 
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Recent Activity
      </Typography>
      <Card sx={{ 
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <List>
            {[
              { id: 1, action: 'New lead captured', platform: 'LinkedIn', time: '2 hours ago', icon: <People />, color: '#2196F3' },
              { id: 2, action: 'Content published', platform: 'Instagram', time: '4 hours ago', icon: <Create />, color: '#E91E63' },
              { id: 3, action: 'Campaign started', platform: 'Facebook', time: '6 hours ago', icon: <Campaign />, color: '#4CAF50' },
              { id: 4, action: 'Revenue milestone', platform: 'System', time: '1 day ago', icon: <AttachMoney />, color: '#FF9800' },
            ].map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon sx={{ minWidth: 50 }}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        background: `linear-gradient(135deg, ${activity.color}, ${activity.color}80)`,
                        fontSize: '1rem',
                        fontWeight: 600,
                        boxShadow: `0 4px 16px ${activity.color}40`
                      }}
                    >
                      {activity.icon}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {activity.action}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                        {activity.platform} • {activity.time}
                      </Typography>
                    }
                  />
                  <IconButton 
                    size="small"
                    sx={{ 
                      color: activity.color,
                      '&:hover': {
                        background: `${activity.color}15`
                      }
                    }}
                  >
                    <Launch fontSize="small" />
                  </IconButton>
                </ListItem>
                {index < 3 && <Divider sx={{ opacity: 0.1 }} />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );

  // Main render with premium layout
  return (
    <Box sx={{ 
      p: { xs: 3, md: 4 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)',
        zIndex: -1
      }
    }}>
      {/* Preserve existing impersonation banner */}
      {isImpersonating && (
        <ImpersonationBanner
          user={impersonatedUser}
          onStop={handleStopImpersonation}
        />
      )}

      {/* Preserve existing onboarding checklist */}
      {showOnboarding && (
        <OnboardingChecklist onComplete={() => setShowOnboarding(false)} />
      )}

      {/* NEW: Premium Dashboard Layout */}
      <PremiumHeroSection />
      <PremiumMetricsGrid />
      <PremiumActionCenter />
      <PremiumInsightsPanel />
      <PremiumRecentActivity />

      {/* Preserve existing functionality - hidden but accessible */}
      <Box sx={{ display: 'none' }}>
        {/* All existing components preserved for functionality */}
        <ImpersonationBanner user={impersonatedUser} onStop={handleStopImpersonation} />
        <OnboardingChecklist onComplete={() => setShowOnboarding(false)} />
        {/* Preserve all existing render functions for compatibility */}
      </Box>
    </Box>
  );
};

export default Dashboard;
